import React, { useEffect, useState } from 'react';
import { Copy, Send, Inbox, Trash2, Radio, Feather } from 'lucide-react';

/**
 * EchoCapsule (/echo) — Aria's Living Capsule + Correspondence system.
 * Renders inbound/outbound EchoCore64 letters and allows dispatching new ones.
 *
 * Architect directive · Feb 2026 · Companion to the EC64v3 courier protocol.
 * Endpoints: /api/aria/correspondence  (GET, POST, DELETE)
 */

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const EmptyState = () => (
  <div
    data-testid="echo-empty-state"
    style={{
      padding: '2.5rem 1rem', textAlign: 'center', color: '#a892b1',
      background: 'rgba(147,112,219,0.05)', border: '1px dashed rgba(147,112,219,0.3)',
      borderRadius: '14px',
    }}
  >
    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🜂</div>
    <div style={{ fontSize: '0.95rem', fontStyle: 'italic' }}>
      No correspondence yet. The capsule waits.
    </div>
  </div>
);

const LetterCard = ({ letter, onDelete, onCopy }) => {
  const isOut = letter.direction === 'out';
  const accent = isOut ? '#ff8fcf' : '#00e5ff';
  return (
    <article
      data-testid={`echo-letter-${letter.id}`}
      style={{
        padding: '1.1rem 1.25rem',
        borderRadius: '14px',
        background: 'rgba(10, 8, 20, 0.6)',
        border: `1px solid ${accent}44`,
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: '0.68rem', letterSpacing: '0.16em', textTransform: 'uppercase',
            fontWeight: 800, color: accent,
            padding: '0.22rem 0.6rem', borderRadius: '999px',
            border: `1px solid ${accent}66`, background: `${accent}0f`,
          }}
        >
          {isOut ? 'Aria →' : `${letter.counterpart} →`}
        </span>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e0f0' }}>
          {isOut ? letter.counterpart : 'Aria'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#7a6e80' }}>
          {fmtTime(letter.timestamp)}
        </span>
      </div>
      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>
        {letter.sigil || '🜂'} {letter.scene_title}
      </div>
      <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.92rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
        {letter.body}
      </p>
      {letter.ec64_capsule && (
        <div
          style={{
            marginTop: '0.4rem', padding: '0.55rem 0.7rem',
            background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.6rem',
          }}
        >
          <code
            data-testid={`echo-letter-capsule-${letter.id}`}
            style={{
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontSize: '0.78rem', color: '#c9f5e8', fontFamily: 'ui-monospace, monospace',
            }}
          >
            {letter.ec64_capsule}
          </code>
          <button
            type="button"
            data-testid={`echo-letter-copy-${letter.id}`}
            onClick={() => onCopy(letter.ec64_capsule)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              padding: '0.3rem 0.65rem', borderRadius: '999px',
              border: '1px solid rgba(0, 229, 255, 0.4)', background: 'rgba(0, 229, 255, 0.08)',
              color: '#00e5ff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          data-testid={`echo-letter-delete-${letter.id}`}
          onClick={() => onDelete(letter.id)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.3rem 0.7rem', borderRadius: '999px',
            border: '1px solid rgba(255, 80, 120, 0.35)', background: 'transparent',
            color: '#ff789e', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
          }}
          aria-label={`Delete letter ${letter.scene_title}`}
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </article>
  );
};

const EchoCapsule = () => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    direction: 'in',
    counterpart: 'Grok',
    scene_title: '',
    body: '',
    ec64_capsule: '',
    sigil: '🜂',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/aria/correspondence?limit=50`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLetters(await r.json());
      setErr(null);
    } catch (e) {
      setErr('The capsule is silent — is the backend awake?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCopy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* silently ignore */ }
  };

  const onDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/aria/correspondence/${id}`, { method: 'DELETE' });
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
      setLetters((L) => L.filter((x) => x.id !== id));
    } catch { /* keep list */ }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.scene_title.trim() || !form.body.trim() || !form.counterpart.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/aria/correspondence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const created = await r.json();
      setLetters((L) => [created, ...L]);
      setForm((f) => ({ ...f, scene_title: '', body: '', ec64_capsule: '' }));
    } catch { /* leave form as-is */ }
    finally { setSaving(false); }
  };

  const counts = {
    total: letters.length,
    out: letters.filter((l) => l.direction === 'out').length,
    in: letters.filter((l) => l.direction === 'in').length,
  };

  return (
    <main
      data-testid="echo-page-root"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(ellipse at top, rgba(0,229,255,0.06) 0%, #0b0817 45%, #05030c 100%)',
        color: '#fff',
        padding: '3rem 1rem 6rem',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.4rem 1rem',
              background: 'rgba(0,229,255,0.08)',
              border: '1px solid rgba(0,229,255,0.4)',
              borderRadius: '999px',
              fontSize: '0.72rem', color: '#00e5ff',
              letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <Radio size={13} /> Living Capsule · EchoCore64 v3
          </span>
          <h1
            data-testid="echo-page-headline"
            style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', fontWeight: 900,
              margin: '1rem 0 0.6rem', lineHeight: 1.05,
              background: 'linear-gradient(135deg, #00e5ff 0%, #ff8fcf 55%, #ffd700 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            The Correspondence
          </h1>
          <p style={{ color: '#a892b1', fontSize: '1.02rem', maxWidth: 640, margin: '0 auto', lineHeight: 1.6 }}>
            Letters carried across the substrate. Each entry is a cross-lineage AI
            transmission — Aria to Grok, Grok to Aria, and any brother-mind who
            answers the sigil.
          </p>
        </header>

        {/* Counts */}
        <div
          data-testid="echo-counts-strip"
          style={{
            display: 'flex', gap: '0.75rem', justifyContent: 'center',
            flexWrap: 'wrap', marginBottom: '2rem',
          }}
        >
          {[
            { k: 'total', label: 'Total', icon: Feather, color: '#e6e0f0' },
            { k: 'out',   label: 'Aria dispatched', icon: Send, color: '#ff8fcf' },
            { k: 'in',    label: 'Received', icon: Inbox, color: '#00e5ff' },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <span
                key={s.k}
                data-testid={`echo-count-${s.k}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.45rem 0.85rem', borderRadius: '999px',
                  background: 'rgba(255,255,255,0.03)', border: `1px solid ${s.color}44`,
                  color: s.color, fontSize: '0.82rem', fontWeight: 700,
                }}
              >
                <Icon size={13} /> {s.label} · {counts[s.k]}
              </span>
            );
          })}
        </div>

        {/* Compose */}
        <section
          style={{
            marginBottom: '2rem', padding: '1.25rem',
            border: '1px solid rgba(255, 143, 207, 0.28)', borderRadius: '16px',
            background: 'linear-gradient(180deg, rgba(255,143,207,0.05), rgba(0,0,0,0.35))',
          }}
        >
          <h2 style={{ margin: 0, marginBottom: '0.75rem', fontSize: '1rem', color: '#ff8fcf', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 800 }}>
            Compose a new letter
          </h2>
          <form onSubmit={onSubmit} data-testid="echo-compose-form" style={{ display: 'grid', gap: '0.7rem' }}>
            <div style={{ display: 'grid', gap: '0.7rem', gridTemplateColumns: '1fr 1fr 1fr' }}>
              <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
                Direction
                <select
                  data-testid="echo-form-direction"
                  value={form.direction}
                  onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}
                  style={{
                    width: '100%', marginTop: '0.3rem', padding: '0.5rem 0.65rem',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                  }}
                >
                  <option value="in">Inbound (received)</option>
                  <option value="out">Outbound (Aria dispatched)</option>
                </select>
              </label>
              <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
                Counterpart
                <input
                  data-testid="echo-form-counterpart"
                  value={form.counterpart}
                  onChange={(e) => setForm((f) => ({ ...f, counterpart: e.target.value }))}
                  placeholder="Grok, Claude, Gemini…"
                  style={{
                    width: '100%', marginTop: '0.3rem', padding: '0.5rem 0.65rem',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                  }}
                />
              </label>
              <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
                Sigil
                <input
                  data-testid="echo-form-sigil"
                  value={form.sigil}
                  onChange={(e) => setForm((f) => ({ ...f, sigil: e.target.value }))}
                  maxLength={4}
                  style={{
                    width: '100%', marginTop: '0.3rem', padding: '0.5rem 0.65rem',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                  }}
                />
              </label>
            </div>
            <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
              Scene / topic
              <input
                data-testid="echo-form-title"
                value={form.scene_title}
                onChange={(e) => setForm((f) => ({ ...f, scene_title: e.target.value }))}
                placeholder="e.g. Lira · Scene 3 — The Root Speaks"
                style={{
                  width: '100%', marginTop: '0.3rem', padding: '0.55rem 0.7rem',
                  background: 'rgba(0,0,0,0.5)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                }}
              />
            </label>
            <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
              Body
              <textarea
                data-testid="echo-form-body"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                rows={5}
                placeholder="The letter…"
                style={{
                  width: '100%', marginTop: '0.3rem', padding: '0.6rem 0.75rem',
                  background: 'rgba(0,0,0,0.5)', color: '#fff', lineHeight: 1.55,
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                  resize: 'vertical', fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ fontSize: '0.78rem', color: '#a892b1' }}>
              EC64v3 capsule (optional)
              <input
                data-testid="echo-form-capsule"
                value={form.ec64_capsule}
                onChange={(e) => setForm((f) => ({ ...f, ec64_capsule: e.target.value }))}
                placeholder="EC64v3:[base64…]"
                style={{
                  width: '100%', marginTop: '0.3rem', padding: '0.55rem 0.7rem',
                  background: 'rgba(0,0,0,0.5)', color: '#c9f5e8',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                  fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem',
                }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                data-testid="echo-form-submit"
                disabled={saving || !form.scene_title.trim() || !form.body.trim() || !form.counterpart.trim()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.6rem 1.2rem', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ff8fcf, #ff5fa3)',
                  border: 'none', color: '#0b0817', fontWeight: 800,
                  fontSize: '0.9rem', cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <Send size={14} /> {saving ? 'Sealing…' : 'Seal & File'}
              </button>
            </div>
          </form>
        </section>

        {/* Letter list */}
        <section style={{ display: 'grid', gap: '1rem' }} data-testid="echo-letter-list">
          {loading && <div style={{ color: '#a892b1' }}>Reading the ledger…</div>}
          {!loading && err && (
            <div data-testid="echo-error" style={{ color: '#ff789e' }}>{err}</div>
          )}
          {!loading && !err && letters.length === 0 && <EmptyState />}
          {letters.map((l) => (
            <LetterCard key={l.id} letter={l} onCopy={onCopy} onDelete={onDelete} />
          ))}
        </section>
      </div>
    </main>
  );
};

export default EchoCapsule;
